import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff } from 'lucide-react';
import { isNative } from '@/native/capacitorShell';
import { registerNativePush } from '@/native/pushNotifications';
import { useAuthStore } from '@/store/authStore';

// Native-only behaviours that need router access: hardware back, deep links, offline banner.
export default function NativeShell() {
  const navigate = useNavigate();
  const [offline, setOffline] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isNative || !isAuthenticated) return;
    registerNativePush(navigate).catch(() => {});
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isNative) return;
    let backHandle, urlHandle, netHandle;

    (async () => {
      const [{ App }, { Network }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/network'),
      ]);

      backHandle = await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack && window.history.length > 1) navigate(-1);
        else App.exitApp();
      });

      urlHandle = await App.addListener('appUrlOpen', ({ url }) => {
        try {
          const parsed = new URL(url);
          navigate(`${parsed.pathname}${parsed.search}${parsed.hash}` || '/');
        } catch (_) { /* non-http scheme */ }
      });

      const status = await Network.getStatus();
      setOffline(!status.connected);
      netHandle = await Network.addListener('networkStatusChange', (s) => setOffline(!s.connected));
    })();

    return () => {
      backHandle?.remove?.();
      urlHandle?.remove?.();
      netHandle?.remove?.();
    };
  }, [navigate]);

  if (!offline) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] bg-red-600 text-white text-xs font-medium px-4 py-2 flex items-center justify-center gap-2"
      data-testid="native-offline-banner"
    >
      <WifiOff className="w-3.5 h-3.5" />
      You are offline — reconnect to continue
    </div>
  );
}
